import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ArticleNotFound from './ArticleNotFound';
import ArticleWide from './ArticleWide';
import ArticleContent from './ArticleContent';
import styled from 'styled-components';
import ArticleCard, { ArticleCardProps } from './ArticleCard';
import { Col } from 'react-bootstrap';

const DestinationAffiliateHeading = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  margin: 0px auto 20px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const ArticleCardStyle = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.small};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: visible;
  margin: ${({ theme }) => theme.spacing.md} auto;
  width: 55%;
  height: 300px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 90%;
    max-width: 500px;
    height: auto;
  }
`;

const ArticleTextContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: right;
`;

const KlookButton = styled.div`
  width: 240px;
  background-color: #fff3f0;
  border: 1px solid #ff5722;
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  color: #ff5722;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #ffe1e1;
    border-color: #ff4b4b;
    color: #ff4b4b;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const GetYourGuideButton = styled.div`
  width: 240px;
  background-color: #fff1f1;
  border: 1px solid #ff4b4b;
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  color: #ff4b4b;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: #ffe1e1;
    border-color: #ff4b4b;
    color: #ff4b4b;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const AffiliatePreviewImage = styled.img`
  width: 50%;
  height: 100%;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;

// New styled components for related articles
const ContentWrapper = styled.div<{ hasRelated?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.hasRelated ? '320px 800px 1fr' : '1fr 800px 1fr'};
  grid-template-areas: 'related main empty';
  max-width: 1400px;
  margin: 0 auto;
  gap: 30px;
  padding: 0 15px;
  direction: rtl; /* Make sure the right column comes first */

  @media (max-width: 1200px) {
    grid-template-columns: ${(props) =>
      props.hasRelated ? '280px 600px 1fr' : '1fr 600px 1fr'};
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'main'
      'related';
    gap: 30px;
  }
`;

const RelatedArticlesContainer = styled.div`
  position: sticky;
  top: 20px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.small};
  padding: 20px;
  margin-right: -50px;
  direction: rtl;
  align-self: flex-start;
  grid-area: related;

  @media (max-width: 992px) {
    position: static;
    width: 320px;
    margin-right: 0;
    margin-bottom: 20px;
    grid-area: related;
    order: 2;
  }
`;
const RelatedArticlesTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  border-bottom: 2px solid ${({ theme }) => theme.colors.surface.light};
  padding-bottom: 10px;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 1;
`;

const ArticleCardWrapper = styled(Col)`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const MainContentContainer = styled.div`
  width: 100%;
  grid-area: main;

  @media (max-width: 992px) {
    width: 320px;
    grid-area: main;
    order: 1;
  }
`;

interface ListItem {
  subtitle?: string;
  text: string;
}

interface SubHeaderItem extends ListItem {
  link?: {
    url: string;
    text: string;
    target?: string;
    rel?: string;
  };
}

interface ArticleProps {
  backButtonLink: string;
  content: {
    title: string;
    imageUrl: string;
    introduction: string;
    sections: {
      title: string;
      content: string | (string | ListItem | SubHeaderItem)[] | string[];
      type: 'text' | 'list' | 'text-image' | 'image' | 'sub-headers';
      imageUrl?: string;
      description?: string;
      sectionLink?: {
        url: string;
        text: string;
      };
    }[];
  };
  destinationName?: string;
  relatedArticles: ArticleCardProps[];

  affiliateData?: {
    previewImageUrl: string;
    klookUrl?: string;
    getYourGuideUrl?: string;
    titleNameOverride?: string;
  };
}

const Article = ({
  backButtonLink,
  content,
  destinationName,
  affiliateData,
  relatedArticles,
}: ArticleProps) => {
  const currentLocation = useLocation();
  const fullUrl = `${window.location.origin}${currentLocation.pathname}`;
  const { previewImageUrl, klookUrl, getYourGuideUrl, titleNameOverride } =
    affiliateData ?? {};
  const hasRelatedArticles = relatedArticles.length > 0;
  const [isMobile, setIsMobile] = useState(false);

  // Get 3 random articles from the relatedArticles array
  const getRandomArticles = (articles: ArticleCardProps[], count: number) => {
    if (articles.length <= count) return articles;

    // Create a copy to avoid modifying the original array
    const shuffled = [...articles].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  // Memoize the random articles to keep them consistent between renders
  const [randomArticles] = useState(() =>
    getRandomArticles(relatedArticles, 3)
  );

  useEffect(() => {
    // Function to check if viewport is mobile
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    // Check initially
    checkIsMobile();

    // Add resize listener
    window.addEventListener('resize', checkIsMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Render affiliate section
  const renderAffiliateSection = () => {
    if (!klookUrl && !getYourGuideUrl) return null;

    return (
      <>
        <DestinationAffiliateHeading>
          כל מה ש{titleNameOverride ?? destinationName} יכולה להציע מחכה לכם כאן
        </DestinationAffiliateHeading>
        <ArticleCardStyle>
          <AffiliatePreviewImage
            id="phuket-affiliate-preview"
            src={previewImageUrl}
            alt={destinationName}
          />
          <ArticleTextContainer>
            <h2 style={{ fontWeight: 'bold' }}>
              {titleNameOverride ?? destinationName}
            </h2>
            <p style={{ fontWeight: '500' }}>
              גלו את הדברים הכי שווים לעשות ב
              {titleNameOverride ?? destinationName}. הזמינו כרטיסים ואטרקציות
              אונליין עם התחייבות למחיר הטוב ביותר! קראו ביקורות על סיורים
              ואטרקציות מובילים ב{titleNameOverride ?? destinationName}.
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '16px',
                marginTop: '20px',
                flexWrap: 'wrap',
              }}
            >
              {/* Klook */}
              {klookUrl && (
                <a
                  href={klookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <KlookButton>
                    <img
                      src="/images/redirects/klookLogo.png"
                      alt="Klook"
                      style={{ height: '20px' }}
                    />
                    הזמן דרך Klook
                  </KlookButton>
                </a>
              )}

              {/* GetYourGuide */}
              {getYourGuideUrl && (
                <a
                  href={getYourGuideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: 'none',
                  }}
                >
                  <GetYourGuideButton>
                    <img
                      src="/images/redirects/getYourGuideLogo.png"
                      alt="GetYourGuide"
                      style={{ height: '20px' }}
                    />
                    הזמן דרך GetYourGuide
                  </GetYourGuideButton>
                </a>
              )}
            </div>
          </ArticleTextContainer>
        </ArticleCardStyle>
      </>
    );
  };

  // Render related articles
  const renderRelatedArticles = () => {
    if (!hasRelatedArticles) return null;

    return (
      <RelatedArticlesContainer>
        <RelatedArticlesTitle>עוד כתבות שאסור לפספס</RelatedArticlesTitle>
        {randomArticles.map((article) => (
          <ArticleCardWrapper key={article.slug}>
            <ArticleCard {...article} />
          </ArticleCardWrapper>
        ))}
      </RelatedArticlesContainer>
    );
  };

  return (
    <>
      {!content ? (
        <ArticleNotFound />
      ) : (
        <>
          <ArticleWide
            title={content.title}
            imageUrl={content.imageUrl}
            backButtonLink={backButtonLink}
          />

          {/* For Desktop - Main Content with Sidebar */}
          {!isMobile && (
            <ContentWrapper hasRelated={hasRelatedArticles}>
              {hasRelatedArticles && renderRelatedArticles()}
              <MainContentContainer>
                <ArticleContent
                  introduction={content.introduction}
                  sections={content.sections}
                  shareUrl={fullUrl}
                  shareTitle={content.title}
                />
              </MainContentContainer>
            </ContentWrapper>
          )}

          {/* For Mobile - Main Content Only */}
          {isMobile && (
            <div
              style={{ maxWidth: '800px', margin: '0 auto', padding: '0 15px' }}
            >
              <MainContentContainer>
                <ArticleContent
                  introduction={content.introduction}
                  sections={content.sections}
                  shareUrl={fullUrl}
                  shareTitle={content.title}
                />
              </MainContentContainer>
            </div>
          )}

          {/* Affiliate Section */}
          {renderAffiliateSection()}

          {/* For Mobile - Related Articles after Affiliate */}
          {isMobile && hasRelatedArticles && (
            <div
              style={{
                maxWidth: '800px',
                margin: '30px auto',
                padding: '0 15px',
              }}
            >
              {renderRelatedArticles()}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Article;
